import { LinkedError, WbUserPreferenceRow, checkAxiosResponse, handleAxiosError } from '@mst-fe/shared';
import { ExceptionStatusType } from 'antd/lib/result';
import axios from 'axios';

import { axiosOptions } from '../../services/requests';

export interface User {
  title: string;
  value: string;
  name: string;
  email?: string;
  selectable: boolean;
}

export interface UserData {
  id: string;
  name: string;
  email?: string;
}

export interface GroupData {
  groupId: string;
  groupName: string;
  members: UserData[];
}

export interface GetGroupsResponse {
  allUserGroups: GroupData[];
}

export interface UserRole {
  isAdmin: boolean;
}

export interface CheckInResult {
  ok: true;
  featureFlags: string[];
  workbenchAllowed: boolean;
  isLsegUser: boolean;
  isAdmin: boolean;
  userPreferences: Omit<WbUserPreferenceRow, 'userSubject'> | undefined;
}

type ApiResponse = {
  ok: boolean;
  statusCode: number;
};

export class UsersApi {
  async getUsers(): Promise<GetGroupsResponse | undefined> {
    return await checkAxiosResponse(axios.get<GetGroupsResponse>('/api/workbench/ownership/my-groups', axiosOptions()));
  }

  async getUserRole(): Promise<UserRole | undefined> {
    return await checkAxiosResponse(axios.get<UserRole>('/api/zoe/user/role', axiosOptions()));
  }

  async getUserGitlabApiKey(): Promise<{ ok: true; data: { gitLabApiKey: string } } | undefined> {
    return await checkAxiosResponse(axios.get<{ ok: true; data: { gitLabApiKey: string } }>('/api/zoe/user/gitlab-apikey', axiosOptions()));
  }

  async putUserGitlabApiKey(apiKey: string): Promise<{ ok: true } | undefined> {
    return await checkAxiosResponse(axios.put<{ ok: true }>('/api/zoe/user/gitlab-apikey', { apiKey }, axiosOptions()));
  }

  async checkIn(): Promise<CheckInResult | undefined> {
    return await checkAxiosResponse(axios.post<CheckInResult>('/api/zoe/user/check-in', {}, axiosOptions()));
  }

  async getUserPreferences() {
    return await checkAxiosResponse(
      axios.get<{ body: WbUserPreferenceRow; ok: boolean } | undefined>('/api/zoe/user/preferences', axiosOptions())
    );
  }

  async saveUserPreferences(userPreferences: Omit<WbUserPreferenceRow, 'userSubject'>) {
    return await checkAxiosResponse(axios.post<{ ok: boolean } | undefined>('/api/zoe/user/preferences', userPreferences, axiosOptions()));
  }

  async cancelUserResourcesDeletion(params: {
    userId: string;
  }): Promise<{ ok: false; statusCode: ExceptionStatusType; error: string } | { ok: true }> {
    const responseOrError = await handleAxiosError(
      axios.put<ApiResponse>(`/api/zoe/user/cancel-user-resources-deletion/${params.userId}`, undefined, axiosOptions())
    );

    if (responseOrError instanceof LinkedError) {
      console.error(responseOrError.cause.responseData);

      if (
        responseOrError.cause.responseStatus === 404 ||
        responseOrError.cause.responseStatus === 403 ||
        responseOrError.cause.responseStatus === 500
      ) {
        const errorMessage =
          responseOrError.cause.responseStatus === 404
            ? 'The user is not in the list of users to be deleted.'
            : responseOrError.cause.responseStatus === 403
              ? 'You are not authorised to perform this action.'
              : 'Sorry, something went wrong.';

        return {
          ok: false,
          statusCode: responseOrError.cause.responseStatus,
          error: errorMessage,
        };
      }

      throw responseOrError;
    }

    return { ok: true };
  }
}
